import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  ShieldCheck,
  Calendar,
  Stethoscope,
  Users,
  Building2,
  Clock,
  FileText,
  Bell
} from 'lucide-react';
import UniversalHeader from '@/components/shared/UniversalHeader';
import UniversalFooter from '@/components/shared/UniversalFooter';

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  const institutionFeatures = [
    {
      title: "Appointment Dashboard",
      description: "Manage daily schedules, view patient queues, and reduce no-shows.",
      icon: Clock
    },
    {
      title: "Student Health Records",
      description: "Secure digital records for each student, accessible during consultations.",
      icon: FileText
    },
    {
      title: "Pre-Visit Triage",
      description: "Review symptoms before patients arrive. Prioritize urgent cases.",
      icon: Stethoscope
    },
    {
      title: "Analytics Dashboard",
      description: "Track clinic utilization, peak hours, and common health trends.",
      icon: Building2
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDF8F6] dark:bg-[#0B1120] text-slate-900 dark:text-white font-sans selection:bg-[#D64463] selection:text-white overflow-x-hidden">
      <UniversalHeader className={scrolled ? 'bg-[#0F172A]/95 text-white shadow-lg' : 'bg-[#0F172A] border-transparent text-white'} />

      {/* Hero Section - Institution First Focus */}
      <section className="relative pt-16 pb-8 lg:pt-24 lg:pb-12 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF0F5] via-[#FDF8F6] to-[#FFE4E1]" />
        <div className="absolute inset-0 z-0 opacity-30">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-400/20 rounded-full blur-[100px] animate-blob" />
          <div className="absolute top-[20%] right-[-10%] w-[40%] h-[40%] bg-purple-400/20 rounded-full blur-[100px] animate-blob animation-delay-2000" />
          <div className="absolute bottom-[-10%] left-[20%] w-[40%] h-[40%] bg-pink-400/20 rounded-full blur-[100px] animate-blob animation-delay-4000" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            <div className="flex-1 text-center lg:text-left space-y-6">

              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/80 backdrop-blur-sm border border-[#D64463]/20 shadow-sm">
                <Building2 className="h-4 w-4 text-[#D64463]" />
                <span className="text-sm font-semibold text-slate-700 tracking-wide">For Forward-Thinking Institutions</span>
              </div>

              {/* Headline */}
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-[#1F303B]">
                The Standard for<br />
                Modern Campus Health.
              </h1>

              {/* Subheadline - Administrator Focused */}
              <p className="text-base md:text-xl text-slate-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-medium">
                Optimizing clinic operations, reducing administrative overhead, and ensuring data-driven care delivery. CareVault Campus empowers your institution to provide world-class health services.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <Button asChild size="lg" className="h-12 px-8 rounded-full bg-[#D64463] hover:bg-[#b0324d] hover:shadow-xl hover:shadow-[#D64463]/20 hover:-translate-y-1 transition-all duration-300 text-base font-bold shadow-lg shadow-[#D64463]/10 w-full sm:w-auto">
                  <Link to="/institution-signup">
                    Schedule a Consultation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="h-12 px-8 rounded-full border-2 border-slate-300 text-slate-700 hover:border-[#D64463] hover:text-[#D64463] font-bold w-full sm:w-auto">
                  <Link to="/register">
                    Student Portal Login
                  </Link>
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-6">
                {[
                  { icon: ShieldCheck, text: "Enterprise-Grade Security", color: "text-emerald-600", bg: "bg-emerald-100" },
                  { icon: Clock, text: "Improved Efficiency", color: "text-blue-600", bg: "bg-blue-100" },
                  { icon: Users, text: "Trusted Partner", color: "text-purple-600", bg: "bg-purple-100" }
                ].map((badge, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-2xl border border-slate-100 shadow-sm">
                    <div className={`p-1.5 rounded-full ${badge.bg}`}>
                      <badge.icon className={`h-4 w-4 ${badge.color}`} />
                    </div>
                    <span className="text-sm font-bold text-slate-700">{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Image - Photorealistic & Relevant */}
            <div className="flex-1 relative w-full max-w-[400px] lg:max-w-[480px]">
              <div className="relative aspect-[4/5] rounded-[3rem] overflow-hidden shadow-2xl border-[8px] border-white ring-1 ring-black/5">
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-10" />
                <img
                  src="/assets/images/campus_hero_v2.png"
                  alt="Doctor consulting with student in a modern health center"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                />

                {/* Floating Card - Institutional Benefit */}
                <div className="absolute bottom-10 left-8 right-8 z-20">
                  <div className="bg-white/95 backdrop-blur-md p-6 rounded-3xl shadow-lg border border-slate-100">
                    <p className="text-lg font-bold text-slate-800 leading-snug">
                      "Getting healthcare right for tertiary institutions is our priority."
                    </p>
                    <p className="text-sm font-medium text-[#D64463] mt-2 uppercase tracking-wide">
                      Campus Health Initiative
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Student Experience */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#0B1120] relative">
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-slate-800 to-transparent" />

        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#D64463] font-bold tracking-wider uppercase text-sm mb-3 block">Enhanced Student Experience</span>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white">A Service Students Trust</h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              Modernize your service delivery to meet the digital expectations of today's students, increasing engagement and satisfaction.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-8 rounded-3xl bg-slate-50 dark:bg-white/5 hover:bg-[#FDF8F6] dark:hover:bg-[#1E293B] hover:shadow-lg hover:-translate-y-2 transition-all duration-300">
              <div className="w-16 h-16 rounded-2xl mb-8 flex items-center justify-center bg-blue-50 shadow-sm">
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900 dark:text-white">Self-Service Booking</h3>
              <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                Reduce front-desk workload by allowing students to book confirmed time slots via their mobile devices.
              </p>
            </div>
            <div className="p-8 rounded-3xl bg-slate-50 dark:bg-white/5 hover:bg-[#FDF8F6] dark:hover:bg-[#1E293B] hover:shadow-lg hover:-translate-y-2 transition-all duration-300">
              <div className="w-16 h-16 rounded-2xl mb-8 flex items-center justify-center bg-purple-50 shadow-sm">
                <FileText className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900 dark:text-white">Digital Pre-Triage</h3>
              <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                Collect symptoms and history before the visit, allowing clinicians to prepare and prioritize effectively.
              </p>
            </div>
            <div className="p-8 rounded-3xl bg-slate-50 dark:bg-white/5 hover:bg-[#FDF8F6] dark:hover:bg-[#1E293B] hover:shadow-lg hover:-translate-y-2 transition-all duration-300">
              <div className="w-16 h-16 rounded-2xl mb-8 flex items-center justify-center bg-amber-50 shadow-sm">
                <Bell className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900 dark:text-white">Campus-Wide Communications</h3>
              <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                Send campus-wide health broadcasts, alerts and notifications. Automatically send appointment reminders and follow-up notifications to reduce no-show rates.
              </p>
            </div>
            <div className="p-8 rounded-3xl bg-slate-50 dark:bg-white/5 hover:bg-[#FDF8F6] dark:hover:bg-[#1E293B] hover:shadow-lg hover:-translate-y-2 transition-all duration-300">
              <div className="w-16 h-16 rounded-2xl mb-8 flex items-center justify-center bg-emerald-50 shadow-sm">
                <Stethoscope className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-900 dark:text-white">Unified Health Record</h3>
              <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                Provide students with secure access to their own records, empowering them to take ownership of their health.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Institutional Control & Analytics */}
      <section className="py-16 md:py-24 bg-[#111827] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at center, #ffffff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#9C27B0]/30 rounded-full blur-[120px] pointer-events-none mix-blend-screen" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#D64463]/20 rounded-full blur-[120px] pointer-events-none mix-blend-screen" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
            <div className="flex-1 space-y-10">
              <div>
                <span className="text-[#D64463] font-bold tracking-wider uppercase text-sm mb-3 block">Institutional Control</span>
                <h2 className="text-4xl md:text-6xl font-bold leading-[0.95] tracking-tight">
                  Data-Driven<br />
                  <span className="text-[#D64463]">Decision Making.</span>
                </h2>
              </div>

              <p className="text-xl text-gray-300 leading-relaxed max-w-xl">
                Gain comprehensive visibility into your health center's performance. Allocate resources effectively and demonstrate value with robust analytics.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {institutionFeatures.map((item, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white/5 rounded-2xl p-5 border border-white/10">
                    <div className="h-12 w-12 rounded-xl bg-white/10 flex items-center justify-center text-[#D64463] flex-shrink-0">
                      <item.icon size={24} />
                    </div>
                    <div>
                      <span className="text-lg font-bold text-white block mb-1">{item.title}</span>
                      <span className="text-sm text-gray-400">{item.description}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <Button asChild className="h-14 px-8 rounded-full bg-white text-slate-900 hover:bg-gray-100 font-bold text-lg">
                  <Link to="/institution-signup">
                    Request a Demo
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Visual: Abstract Representation of Connectivity/Success instead of Dashboard UI */}
            <div className="flex-1 w-full max-w-lg">
              <div className="relative aspect-square rounded-full border-2 border-white/10 flex items-center justify-center">
                <div className="absolute inset-0 bg-[#D64463]/5 rounded-full animate-pulse" />

                {/* Central Node */}
                <div className="h-32 w-32 bg-white rounded-full flex items-center justify-center shadow-[0_0_60px_rgba(255,255,255,0.3)] z-10 relative">
                  <Building2 className="text-[#111827] h-12 w-12" />
                  {/* Orbiting nodes */}
                  <div className="absolute -right-16 top-0 bg-[#1E293B] p-3 rounded-xl border border-white/20 shadow-lg animate-bounce delay-100">
                    <Users className="text-white h-6 w-6" />
                  </div>
                  <div className="absolute -left-12 bottom-0 bg-[#1E293B] p-3 rounded-xl border border-white/20 shadow-lg animate-bounce delay-300">
                    <Clock className="text-white h-6 w-6" />
                  </div>
                  <div className="absolute top-[-30px] bg-[#1E293B] p-3 rounded-xl border border-white/20 shadow-lg animate-bounce delay-500">
                    <ShieldCheck className="text-white h-6 w-6" />
                  </div>
                </div>

                <p className="absolute bottom-10 font-bold text-white/50 tracking-widest text-sm uppercase">Connected Campus Ecosystem</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 relative overflow-hidden bg-[#D64463]">
        <div className="absolute inset-0 bg-[url('/assets/pattern.svg')] opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#D64463] to-[#9C27B0] mix-blend-multiply" />

        <div className="container mx-auto px-4 relative z-10 text-center space-y-8 max-w-4xl">
          <h2 className="text-4xl md:text-7xl font-black text-white tracking-tight mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto font-medium">
            Join universities and polytechnics across Nigeria already using CareVault Campus.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 pt-8">
            <Button asChild size="lg" className="h-16 md:h-20 px-10 md:px-12 rounded-full bg-white text-[#D64463] text-lg md:text-2xl font-bold hover:scale-105 transition-transform shadow-2xl hover:bg-slate-50 border-4 border-transparent">
              <Link to="/register">I'm a Student</Link>
            </Button>
            <Button asChild size="lg" className="h-16 md:h-20 px-10 md:px-12 rounded-full bg-[#1F303B] text-white text-lg md:text-2xl font-bold hover:bg-[#334155] border-4 border-transparent shadow-xl transition-all">
              <Link to="/institution-signup">I'm an Institution</Link>
            </Button>
          </div>

        </div>
      </section>

      {/* Small link for general providers */}
      <section className="py-8 bg-slate-50 dark:bg-[#1E293B]">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-600 dark:text-slate-400">
            Not a student or institution? <Link to="/providers" className="text-[#D64463] font-semibold hover:underline">CareVault for General Providers →</Link>
          </p>
        </div>
      </section>

      <div className="bg-[#0F172A]">
        <UniversalFooter />
      </div>
    </div>
  );
}
